import type { Lang, PageKey } from "./types";
import routeData from "./routes.json";

// =========================================================================
// URL ROUTING — derived from routes.json, the single source of truth
// =========================================================================
//
// Each page owns one real, crawlable URL per language. Spanish lives under an
// /es prefix with translated slugs so the Spanish tree targets Spanish search
// queries directly (e.g. "camaras de seguridad tampa") rather than reusing the
// English keyword in the path.
//
// routes.json is shared with scripts/generate-sitemap.js so sitemap.xml, the
// react-snap prerender list, and client routing can never disagree about what
// URLs exist. Add pages there, not here.

export const BASE_URL: string = routeData.baseUrl;

type RouteEntry = { en: string; es: string; priority: string; changefreq: string };
const PAGES = routeData.pages as Record<PageKey, RouteEntry>;

// Built with a plain loop rather than Object.fromEntries: the build targets
// es5 and does not polyfill it, so it would break on older browsers.
export const ROUTES: Record<PageKey, Record<Lang, string>> = {} as Record<
  PageKey,
  Record<Lang, string>
>;
(Object.keys(PAGES) as PageKey[]).forEach((page) => {
  ROUTES[page] = { en: PAGES[page].en, es: PAGES[page].es };
});

/** Relative path for a page in a given language. */
export function pathFor(page: PageKey, lang: Lang): string {
  return ROUTES[page][lang];
}

/** Absolute URL for a page in a given language (canonical / hreflang / sitemap). */
export function urlFor(page: PageKey, lang: Lang): string {
  const path = pathFor(page, lang);
  return path === "/" ? `${BASE_URL}/` : `${BASE_URL}${path}`;
}

/** Strip trailing slashes (but keep the root "/") for stable lookups. */
function normalize(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

// Reverse lookup built once at module load: "/es/servicios" -> {page, lang}.
const PATH_LOOKUP: Record<string, { page: PageKey; lang: Lang }> = {};
(Object.keys(ROUTES) as PageKey[]).forEach((page) => {
  (Object.keys(ROUTES[page]) as Lang[]).forEach((lang) => {
    PATH_LOOKUP[normalize(ROUTES[page][lang])] = { page, lang };
  });
});

/**
 * Resolve a browser pathname to a page + language.
 *
 * Unknown paths fall back to the home page of whichever language tree the URL
 * sits in, so a stale or mistyped /es/... link still lands a Spanish speaker on
 * Spanish content instead of bouncing them into English.
 */
export function resolveLocation(pathname: string): { page: PageKey; lang: Lang } {
  const path = normalize(pathname);
  const match = PATH_LOOKUP[path];
  if (match) return match;
  const lang: Lang = path === "/es" || path.startsWith("/es/") ? "es" : "en";
  return { page: "home", lang };
}

/** Flat list of every URL, used by the sitemap generator and prerenderer. */
export const ALL_ROUTES: Array<{ page: PageKey; lang: Lang; path: string }> = (
  Object.keys(ROUTES) as PageKey[]
).flatMap((page) =>
  (Object.keys(ROUTES[page]) as Lang[]).map((lang) => ({
    page,
    lang,
    path: ROUTES[page][lang],
  }))
);
